import { useCallback, useEffect, useState } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import {
  PaymentsPayinService,
  type PayinRuntimeEntity,
  PayinRuntimeStatus,
} from "@/modules/dashboard/payins/services/paymentsPayin.service";
import { UserType } from "@/shared/types/user.entity";

import type { RuntimeWallet } from "../types/payments-wallets.types";

/** Cap on the section: these are open collections, not a history. More than this is a listing problem. */
export const PENDING_COLLECTIONS_LIMIT = 20;
/** Same cadence as payments-activity — there is no websocket for payins reaching a wallet. */
export const PENDING_COLLECTIONS_POLL_MS = 15_000;

/**
 * The statuses in which a collection is still waiting for the payer's money. `CREATED` is an instrument that
 * was issued (a QR, a dynamic CLABE) and nothing has arrived; `PENDING` means the provider detected the
 * payment and it is parked. Both are invisible in the wallet statement, which is exactly why this exists.
 */
const AWAITING_STATUSES = [PayinRuntimeStatus.CREATED, PayinRuntimeStatus.PENDING] as const;

export interface UseWalletPendingCollectionsResult {
  collections: PayinRuntimeEntity[];
  /** Re-read now — used when a collection may have just left the band (its drawer closed). */
  refresh: () => void;
}

/**
 * The collections this wallet is waiting on — the money the hero already counts as `balance_pending`,
 * itemised.
 *
 * They cannot come from the statement: `GET /wallets/:id/transactions` lists ledger legs, and an unpaid payin
 * has none (the client-liability account is only credited on confirmation, and a detected payin posts to the
 * suspense accounts instead). So the rows come from the payins list, matched to the wallet by currency —
 * which is exact, not an approximation: the runtime holds exactly one wallet per (organization, currency).
 *
 * `status` on the list endpoint is single-valued, hence one request per awaiting status.
 *
 * There is no loading flag and no error: this band is supplementary to a statement that must keep rendering
 * on its own. While the first read is in flight it shows nothing, and a failed read keeps whatever was last
 * known rather than blanking — flashing "no pending collections" at someone who has some would be a lie, and
 * the next read corrects it either way.
 */
export const useWalletPendingCollections = (
  wallet: RuntimeWallet | null,
): UseWalletPendingCollectionsResult => {
  const { user } = useAuth();
  const [collections, setCollections] = useState<PayinRuntimeEntity[]>([]);
  // Bumping this is what triggers a read: the poll and the manual refresh both go through it, so there is one
  // request path instead of a shared async function two effects call.
  const [readToken, setReadToken] = useState(0);
  const refresh = useCallback(() => setReadToken((token) => token + 1), []);

  const currency = wallet?.currency ?? null;
  // Org members are pinned server-side and must not send the filter; INTERNAL staff read a client's wallet, so
  // the org has to come from the wallet itself or the query would span every organization.
  const organizationId = user?.user_type === UserType.INTERNAL ? (wallet?.organization_id ?? null) : null;

  useEffect(() => {
    // Nothing to clear on the way out: the band starts empty, nothing populates it without a currency, and a
    // currency that changes replaces the rows through the read below.
    if (!currency) return;
    let cancelled = false;
    void (async () => {
      try {
        const pages = await Promise.all(
          AWAITING_STATUSES.map((status) =>
            PaymentsPayinService.listPayins({
              organizationId,
              currency,
              status,
              limit: PENDING_COLLECTIONS_LIMIT,
            }),
          ),
        );
        if (cancelled) return;
        setCollections(
          pages
            .flatMap((page) => page.data.data)
            .sort((a, b) => b.created_at.localeCompare(a.created_at))
            .slice(0, PENDING_COLLECTIONS_LIMIT),
        );
      } catch {
        // Keep the last known rows — see the note on the hook.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currency, organizationId, readToken]);

  /**
   * Every row here is non-terminal by definition, so polling runs exactly while there is something to watch —
   * and stops on an empty band instead of asking twice a minute about a wallet with nothing pending. The
   * known consequence: a collection created in another tab appears on the next mount, not while this page
   * sits idle. That is the normal path anyway (collections are created from another screen, so coming back
   * remounts), and the runtime emits no event on creation for a socket to pick up either.
   */
  useEffect(() => {
    if (collections.length === 0) return;
    const timer = setInterval(refresh, PENDING_COLLECTIONS_POLL_MS);
    return () => clearInterval(timer);
  }, [collections.length, refresh]);

  return { collections, refresh };
};
